import React from 'react';
import { COINS } from '../../constants';

/**
 * AdminPoolTable — Breakdown of liquidity pools by coin.
 * Shows UP/DOWN split, total volume, and imbalance for each coin.
 */
const AdminPoolTable = ({ pools }) => {
    // Aggregate pools by coin
    const coinPools = COINS.map(coin => {
        let totalUp = 0;
        let totalDown = 0;
        let slotCount = 0;

        Object.entries(pools).forEach(([key, pool]) => {
            if (key.startsWith(coin.id + '_')) {
                totalUp += pool.up || 0;
                totalDown += pool.down || 0;
                if (pool.up > 0 || pool.down > 0) slotCount++;
            }
        });

        const total = totalUp + totalDown;
        const imbalance = total > 0
            ? Math.abs(totalUp - totalDown) / total * 100
            : 0;

        return {
            coin,
            totalUp,
            totalDown,
            total,
            slotCount,
            imbalance,
            lean: totalUp > totalDown ? 'UP' : totalDown > totalUp ? 'DOWN' : '—',
        };
    });

    const grandTotal = coinPools.reduce((a, c) => a + c.total, 0);

    return (
        <div className="border border-white/5 bg-white/[0.02] rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-white/5 flex justify-between items-center">
                <h3 className="text-sm uppercase tracking-[0.25em] text-gray-400 font-black">
                    Pool Breakdown
                </h3>
                <span className="text-sm font-mono text-gray-500">
                    Total: ${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
            </div>

            <table className="w-full text-sm">
                <thead>
                    <tr className="text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-white/5">
                        <th className="px-5 py-2 text-left font-bold">Coin</th>
                        <th className="px-3 py-2 text-right font-bold">UP Pool</th>
                        <th className="px-3 py-2 text-right font-bold">DOWN Pool</th>
                        <th className="px-3 py-2 text-right font-bold">Total</th>
                        <th className="px-3 py-2 text-right font-bold">Slots</th>
                        <th className="px-3 py-2 text-right font-bold">Lean</th>
                        <th className="px-5 py-2 text-right font-bold">Imbalance</th>
                    </tr>
                </thead>
                <tbody>
                    {coinPools.map(({ coin, totalUp, totalDown, total, slotCount, imbalance, lean }) => (
                        <tr key={coin.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                            <td className="px-5 py-2.5 font-mono font-bold text-white">{coin.symbol}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-green-400">
                                ${totalUp.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono text-red-400">
                                ${totalDown.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono text-white">
                                ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono text-gray-400">{slotCount}</td>
                            <td className={`px-3 py-2.5 text-right font-mono font-bold ${lean === 'UP' ? 'text-green-400' : lean === 'DOWN' ? 'text-red-400' : 'text-gray-600'}`}>
                                {lean}
                            </td>
                            <td className="px-5 py-2.5 text-right font-mono text-amber-400">
                                {imbalance.toFixed(1)}%
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default AdminPoolTable;
